'use client'

import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PropertyWithLandlord, SearchFilters } from '@/types/client'
import { getApiUrl } from '@/lib/apiUrl'

interface PropertySearchProps {
  onPropertiesChange: (properties: PropertyWithLandlord[]) => void
  onLoadingChange: (loading: boolean) => void
}

const DEFAULT_FILTERS: SearchFilters = {
  priceRange: [0, 5001],
  location: '',
  propertyType: '',
  rentalType: 'all',
  amenities: [],
  sortBy: 'newest',
  duration: ''
}

export default function PropertySearch({ onPropertiesChange, onLoadingChange }: PropertySearchProps) {
  const [filters, setFilters] = useState<SearchFilters>(DEFAULT_FILTERS)
  const [showFilters, setShowFilters] = useState(false)
  const [loading, setLoading] = useState(false)
  const [searchParams] = useSearchParams()

  // Handle URL parameters for similar properties search
  useEffect(() => {
    const location = searchParams.get('location')
    const priceMin = searchParams.get('price_min')
    const priceMax = searchParams.get('price_max')

    if (location || priceMin || priceMax) {
      const newFilters = { ...filters }
      
      if (location) {
        newFilters.location = location
      }
      
      if (priceMin && priceMax) {
        newFilters.priceRange = [parseInt(priceMin), parseInt(priceMax)]
      }
      
      setFilters(newFilters)
      
      // Auto-search with new filters
      setTimeout(() => {
        searchProperties(newFilters)
      }, 100)
    }
  }, [searchParams])

  // Nigeria-focused types and expanded amenities
  // Note: backend expects a smaller enum, so we map frontend values -> backend values.
  const propertyTypes = [
    { value: '', label: 'All Types' },
    { value: 'self_contain', label: 'Self Contain (Studio)', backendValue: 'studio' },
    { value: 'mini_flat', label: 'Mini Flat (1 Bedroom)', backendValue: 'apartment' },
    { value: 'single_room', label: 'Single Room (1 Bedroom)', backendValue: 'apartment' },
    { value: 'room_palor', label: 'Room and Palor', backendValue: 'apartment' },
    { value: 'one_bedroom', label: '1 Bedroom Flat', backendValue: 'apartment' },
    { value: 'two_bedroom', label: '2 Bedroom Flat', backendValue: 'apartment' },
    { value: 'three_bedroom', label: '3 Bedroom Flat', backendValue: 'apartment' },
    { value: 'four_bedroom', label: '4 Bedroom Flat', backendValue: 'apartment' },
    { value: 'five_bedroom', label: '5 Bedroom Flat', backendValue: 'apartment' },
    { value: 'six_bedroom', label: '6 Bedroom Flat', backendValue: 'apartment' },
    { value: 'seven_bedroom', label: '7 Bedroom Flat', backendValue: 'apartment' },
    { value: 'bungalow', label: 'Bungalow', backendValue: 'house' },
    { value: 'detached_duplex', label: 'Detached Duplex', backendValue: 'house' },
    { value: 'semi_detached_duplex', label: 'Semi-Detached Duplex', backendValue: 'house' },
    { value: 'terraced_duplex', label: 'Terraced Duplex', backendValue: 'townhouse' },
    { value: 'mansion', label: 'Mansion', backendValue: 'house' },
    { value: 'penthouse', label: 'Penthouse', backendValue: 'apartment' },
    { value: 'shop', label: 'Shop/Office Space', backendValue: 'other' },
    { value: 'warehouse', label: 'Warehouse', backendValue: 'other' }
  ]

  const amenitiesOptions = [
    { value: 'parking', label: 'Parking' },
    { value: 'security', label: 'Security' },
    { value: 'gated_estate', label: 'Gated Estate' },
    { value: 'cctv', label: 'CCTV' },
    { value: '24_7_electricity', label: '24/7 Electricity' },
    { value: 'inverter_backup', label: 'Inverter/Backup Power' },
    { value: 'prepaid_meter', label: 'Prepaid Meter' },
    { value: 'solar_power', label: 'Solar Power' },
    { value: 'generator_house', label: 'Generator House' },
    { value: 'borehole', label: 'Borehole' },
    { value: 'water_supply', label: 'Water Supply' },
    { value: 'water_heater', label: 'Water Heater' },
    { value: 'fitted_kitchen', label: 'Fitted Kitchen' },
    { value: 'cooker', label: 'Cooker' },
    { value: 'microwave', label: 'Microwave' },
    { value: 'refrigerator', label: 'Refrigerator' },
    { value: 'wardrobes', label: 'Wardrobes' },
    { value: 'pop_ceiling', label: 'POP Ceiling' },
    { value: 'air_conditioning', label: 'Air Conditioning' },
    { value: 'balcony', label: 'Balcony' },
    { value: 'elevator', label: 'Elevator' },
    { value: 'swimming_pool', label: 'Swimming Pool' },
    { value: 'gym', label: 'Gym' },
    { value: 'playground', label: 'Playground' },
    { value: 'internet', label: 'Internet' },
    { value: 'interlocked_compound', label: 'Interlocked Compound' },
    { value: 'paved_road_access', label: 'Paved Road Access' },
    { value: 'ensuite_rooms', label: 'All Rooms Ensuite' },
    { value: 'visitors_toilet', label: 'Visitor’s Toilet' },
    { value: 'washing_machine', label: 'Washing Machine' },
    { value: 'green_area', label: 'Green Area' },
    { value: 'study', label: 'Study' },
    { value: 'boys_quarters', label: 'Boys’ Quarters (BQ)' },
    { value: 'jacuzzi', label: 'Jacuzzi' }
  ]

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'price_low', label: 'Price: Low to High' },
    { value: 'price_high', label: 'Price: High to Low' },
    { value: 'popular', label: 'Most Popular' }
  ]

  const durationOptions = [
    { value: '', label: 'Any Duration' },
    { value: '6', label: '6 Months' },
    { value: '12', label: '1 Year' },
    { value: '18', label: '18 Months' },
    { value: '24', label: '2 Years' }
  ]

  // Search properties with filters
  const searchProperties = async (overrideFilters?: SearchFilters) => {
    setLoading(true)
    onLoadingChange(true)

    try {
      const active = overrideFilters ?? filters

      const queryParams = new URLSearchParams()
      // Backend already filters by isVerified: true, but we can be explicit
      queryParams.set('verified', 'true')
      if (active.priceRange[0] > 0 || active.priceRange[1] < 5001) {
        queryParams.set('minPrice', active.priceRange[0].toString())
        queryParams.set('maxPrice', active.priceRange[1].toString())
      }
      if (active.location) queryParams.set('location', active.location)
      if (active.propertyType) {
        const type = propertyTypes.find(t => t.value === active.propertyType)
        queryParams.set('propertyType', (type as any)?.backendValue || active.propertyType)
      }
      if (active.rentalType && active.rentalType !== 'all') queryParams.set('rentalType', active.rentalType)
      if (active.duration) queryParams.set('duration', active.duration)
      if (active.amenities.length > 0) queryParams.set('amenities', active.amenities.join(','))

      // Map UI sort options -> backend supported sort fields
      if (active.sortBy) {
        switch (active.sortBy) {
          case 'newest':
            queryParams.set('sortBy', 'createdAt')
            queryParams.set('sortOrder', 'desc')
            break
          case 'oldest':
            queryParams.set('sortBy', 'createdAt')
            queryParams.set('sortOrder', 'asc')
            break
          case 'price_low':
            queryParams.set('sortBy', 'price')
            queryParams.set('sortOrder', 'asc')
            break
          case 'price_high':
            queryParams.set('sortBy', 'price')
            queryParams.set('sortOrder', 'desc')
            break
          case 'popular':
            queryParams.set('sortBy', 'views')
            queryParams.set('sortOrder', 'desc')
            break
          default:
            queryParams.set('sortBy', 'createdAt')
            queryParams.set('sortOrder', 'desc')
            break
        }
      }

      console.log('🔍 PropertySearch: Executing search with filters:', active)
      console.log('🔍 PropertySearch: Query params:', queryParams.toString())
      const apiUrl = getApiUrl()
      const fullUrl = `${apiUrl}/properties?${queryParams.toString()}`
      console.log('🔍 PropertySearch: Full URL:', fullUrl)
      
      const response = await fetch(fullUrl, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ PropertySearch: Error searching properties:', response.status, response.statusText, errorText)
        onPropertiesChange([])
      } else {
        const data = await response.json()
        console.log('✅ PropertySearch: Raw API response:', data)
        // Transform properties to match frontend format
        // const propertiesArray = data.properties || data || []
       
        const propertiesArray = Array.isArray(data)
        ? data
        : Array.isArray(data.properties)
          ? data.properties
          : data._id
            ? [data]   // wrap single object into an array
            : []
      
        console.log('✅ PropertySearch: Properties array length:', propertiesArray.length)
        const transformedProperties = propertiesArray.map((prop: any) => ({
          ...prop,
          id: prop._id || prop.id,
          // Extract and normalize image URLs
          images: (() => {
            const imageUrls = prop.images?.map((img: any) => typeof img === 'string' ? img : img.url) || prop.images || [];
            // Normalize localhost URLs to use backend URL from environment
            return imageUrls.map((url: string) => {
              if (!url) return url;
              if (url.includes('localhost:5001') || url.includes('127.0.0.1:5001')) {
                const apiUrl = getApiUrl();
                const apiBaseUrl = apiUrl.replace(/\/api\/?$/, '');
                try {
                  const urlObj = new URL(url);
                  return `${apiBaseUrl}${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
                } catch {
                  return url.replace(/https?:\/\/[^/]+/, apiBaseUrl);
                }
              } else if (url.startsWith('/uploads')) {
                const apiUrl = getApiUrl();
                const apiBaseUrl = apiUrl.replace(/\/api\/?$/, '');
                return `${apiBaseUrl}${url}`;
              }
              return url;
            });
          })(),
          // Build location from address
          location: prop.location || (prop.address && typeof prop.address === 'object' 
            ? [prop.address.street, prop.address.city, prop.address.state].filter(Boolean).join(', ')
            : 'Location not specified'),
          // Map verification status
          is_verified: prop.isVerified !== undefined ? prop.isVerified : prop.is_verified,
          is_available: prop.isAvailable !== undefined ? prop.isAvailable : prop.is_available,
          created_at: prop.createdAt || prop.created_at
        }))
        console.log('✅ PropertySearch: Transformed properties:', transformedProperties.length, 'properties found')
        console.log('✅ PropertySearch: Sample property:', transformedProperties[0])
        onPropertiesChange(transformedProperties)
      }
    } catch (error) {
      console.error('Error in searchProperties:', error)
      onPropertiesChange([])
    } finally {
      setLoading(false)
      onLoadingChange(false)
    }
  }

  // Handle filter changes
  const handleFilterChange = <K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }))
  }

  // Handle price range change
  const handlePriceRangeChange = (min: number, max: number) => {
    setFilters(prev => ({
      ...prev,
      priceRange: [min, max]
    }))
  }

  // Handle amenities toggle
  const handleAmenityToggle = (amenity: string) => {
    setFilters(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }))
  }

  // Clear all filters
  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS)
  }

  // Auto-search on mount to load verified properties
  useEffect(() => {
    // Only auto-search if there are no URL parameters (to avoid duplicate searches)
    const hasUrlParams = searchParams.get('location') || searchParams.get('price_min') || searchParams.get('price_max')
    if (!hasUrlParams) {
      // Trigger initial search to load verified properties
      // Use a small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        searchProperties(DEFAULT_FILTERS)
      }, 100)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Run once on mount

  // Remove automatic search on filter change for now
  // Users will manually click "Apply Filters" or "Show All Properties"

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-6">
      {/* Search Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-display text-stone-900">Search Properties</h2>
          {searchParams.get('location') && (
            <p className="text-sm text-emerald-600 mt-1 font-medium">
              🔍 Showing similar properties near {searchParams.get('location')}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {searchParams.get('location') ? (
            <button
              onClick={() => {
                setFilters(DEFAULT_FILTERS)
                searchProperties(DEFAULT_FILTERS)
                // Clear URL parameters by navigating to dashboard without search params
                window.history.replaceState({}, '', '/dashboard/client')
              }}
              className="text-amber-600 hover:text-amber-700 text-sm font-semibold"
            >
              Clear Similar Properties Search
            </button>
          ) : (
            <button
              onClick={() => {
                setFilters(DEFAULT_FILTERS)
                searchProperties(DEFAULT_FILTERS)
              }}
              className="text-emerald-600 hover:text-emerald-700 text-sm font-semibold"
            >
              Show All Properties
            </button>
          )}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="text-stone-600 hover:text-stone-900 text-sm font-semibold"
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>
      </div>

      {/* Quick Search Bar */}
      <div className="flex gap-3 mb-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by location..."
            value={filters.location}
            onChange={(e) => handleFilterChange('location', e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                searchProperties()
              }
            }}
            className="w-full px-4 py-2.5 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
          />
        </div>
        <select
          value={filters.sortBy}
          onChange={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleFilterChange('sortBy', e.target.value)
          }}
          onClick={(e) => {
            e.stopPropagation()
          }}
          className="px-4 py-2.5 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors cursor-pointer bg-white text-stone-900"
          style={{ pointerEvents: 'auto' }}
        >
          {sortOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
          <button
            onClick={() => searchProperties()}
          disabled={loading}
          className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/25 font-semibold"
        >
          {loading ? (
            <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          ) : (
            'Apply Filters'
          )}
        </button>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="border-t pt-4 space-y-4">
          {/* Price Range */}
          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-2">
              Price Range: ₦{filters.priceRange[0].toLocaleString()} - ₦{filters.priceRange[1].toLocaleString()}
            </label>
            <div className="flex gap-4 items-center">
              <input
                type="range"
                min="0"
                max="5001"
                step="100"
                value={filters.priceRange[0]}
                onChange={(e) => handlePriceRangeChange(parseInt(e.target.value) || 0, filters.priceRange[1])}
                className="flex-1"
              />
              <input
                type="range"
                min="0"
                max="5001"
                step="100"
                value={filters.priceRange[1]}
                onChange={(e) => handlePriceRangeChange(filters.priceRange[0], parseInt(e.target.value) || 5001)}
                className="flex-1"
              />
            </div>
          </div>

          {/* Property Type and Duration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-2">Property Type</label>
              <select
                value={filters.propertyType}
                onChange={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleFilterChange('propertyType', e.target.value)
                }}
                onClick={(e) => {
                  e.stopPropagation()
                }}
                className="w-full px-3 py-2.5 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-stone-900 transition-colors cursor-pointer"
                style={{ pointerEvents: 'auto' }}
              >
                {propertyTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-2">Lease Duration</label>
              <select
                value={filters.duration}
                onChange={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleFilterChange('duration', e.target.value)
                }}
                onClick={(e) => {
                  e.stopPropagation()
                }}
                className="w-full px-3 py-2.5 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-stone-900 transition-colors cursor-pointer"
                style={{ pointerEvents: 'auto' }}
              >
                {durationOptions.map(duration => (
                  <option key={duration.value} value={duration.value}>
                    {duration.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Amenities */}
          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-2">Amenities</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {amenitiesOptions.map(amenity => (
                <label key={amenity.value} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={filters.amenities.includes(amenity.value)}
                    onChange={() => handleAmenityToggle(amenity.value)}
                    className="rounded border-stone-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-stone-700">{amenity.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Clear Filters */}
          <div className="flex justify-end">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-stone-600 hover:text-stone-900 text-sm font-semibold transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        </div>
      )}
    </div>
  )
} 

